import { apiClient } from "@/lib/api-client"
import {
  WorkflowDto,
  WorkflowTaskDto,
  UpsertWorkflowDto,
  UpsertWorkflowTaskDto,
} from "../workflows/dtos/workflow.dto"

class WorkflowService {
  async getWorkflows(
    orgId: string,
    includeTasks = true,
  ): Promise<WorkflowDto[]> {
    const { data } = await apiClient.get<WorkflowDto[]>(
      `/organizations/${orgId}/workflows`,
      {
        params: { includeTasks },
      },
    )
    return data
  }

  async createWorkflow(
    orgId: string,
    payload: UpsertWorkflowDto,
  ): Promise<WorkflowDto> {
    const { data } = await apiClient.post<WorkflowDto>(
      `/organizations/${orgId}/workflows`,
      payload,
    )
    return data
  }

  async updateWorkflow(
    orgId: string,
    id: string,
    payload: UpsertWorkflowDto,
  ): Promise<WorkflowDto> {
    const { data } = await apiClient.put<WorkflowDto>(
      `/organizations/${orgId}/workflows/${id}`,
      payload,
    )
    return data
  }

  async deleteWorkflow(orgId: string, id: string): Promise<void> {
    await apiClient.delete(`/organizations/${orgId}/workflows/${id}`)
  }

  // Tasks
  async getTasks(
    orgId: string,
    workflowId: string,
  ): Promise<WorkflowTaskDto[]> {
    const { data } = await apiClient.get<WorkflowTaskDto[]>(
      `/organizations/${orgId}/workflows/${workflowId}/tasks`,
    )
    return data
  }

  async createTask(
    orgId: string,
    workflowId: string,
    payload: UpsertWorkflowTaskDto,
  ): Promise<WorkflowTaskDto> {
    const { data } = await apiClient.post<WorkflowTaskDto>(
      `/organizations/${orgId}/workflows/${workflowId}/tasks`,
      payload,
    )
    return data
  }

  async updateTask(
    orgId: string,
    workflowId: string,
    taskId: string,
    payload: UpsertWorkflowTaskDto,
  ): Promise<WorkflowTaskDto> {
    const { data } = await apiClient.put<WorkflowTaskDto>(
      `/organizations/${orgId}/workflows/${workflowId}/tasks/${taskId}`,
      payload,
    )
    return data
  }

  async deleteTask(
    orgId: string,
    workflowId: string,
    taskId: string,
  ): Promise<void> {
    await apiClient.delete(
      `/organizations/${orgId}/workflows/${workflowId}/tasks/${taskId}`,
    )
  }

  async startWorkflow(
    orgId: string,
    workflowId: string,
    payload: {
      contextType: "Employee" | "Standalone"
      employeeId?: string
      contextDescription?: string
      batchId?: string
      batchName?: string
      taskDueDateOverrides?: {
        workflowTaskId: string
        dueDate: string | null
      }[]
    },
  ): Promise<{ id: string; workflowId: string; status: string }> {
    const { data } = await apiClient.post(
      `/organizations/${orgId}/workflow-states/${workflowId}/start`,
      payload,
    )
    return data
  }
}

export const workflowService = new WorkflowService()
